import React, { useEffect, useState, useRef } from "react";
import { http, PLATFORM_TONES } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, Image as ImageIcon, X } from "lucide-react";
import PlatformResultCard from "@/components/PlatformResultCard";

async function fileToBase64(file) {
  const b = await file.arrayBuffer();
  const bin = String.fromCharCode(...new Uint8Array(b));
  return btoa(bin);
}

export default function GenerateTab({ onRun, prefill }) {
  const [platforms, setPlatforms] = useState([]);
  const [selected, setSelected] = useState(["etsy", "amazon_merch", "redbubble"]);
  const [productName, setProductName] = useState("");
  const [productType, setProductType] = useState("");
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("professional");
  const [language, setLanguage] = useState("English");
  const [cleanMode, setCleanMode] = useState(true);
  const [image, setImage] = useState(null);
  const [brandVoices, setBrandVoices] = useState([]);
  const [brandVoiceId, setBrandVoiceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    http.get("/platforms").then(r => setPlatforms(r.data)).catch(()=>{});
    http.get("/brand-voice").then(r => setBrandVoices(r.data)).catch(()=>{});
  }, []);

  // Apply prefill from history re-run
  useEffect(() => {
    if (!prefill) return;
    if (prefill.productName) setProductName(prefill.productName);
    if (prefill.productType) setProductType(prefill.productType);
    if (prefill.keywords) setKeywords((prefill.keywords || []).join(", "));
    if (prefill.platforms) setSelected(prefill.platforms);
    if (prefill.tone) setTone(prefill.tone);
    if (prefill.language) setLanguage(prefill.language);
    if (typeof prefill.cleanMode === "boolean") setCleanMode(prefill.cleanMode);
    toast.success("Loaded from history");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const togglePlatform = (k) => {
    setSelected(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);
  };
  const selectAll = () => setSelected(platforms.map(p => p.key));
  const clearAll = () => setSelected([]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(jpeg|jpg|png|webp)/i.test(f.type)) { toast.error("Use JPEG, PNG or WEBP"); return; }
    const b64 = await fileToBase64(f);
    setImage({ b64, name: f.name, mime: f.type, preview: `data:${f.type};base64,${b64}` });
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && fileRef.current) {
      const dt = new DataTransfer(); dt.items.add(f);
      fileRef.current.files = dt.files;
      onFile({ target: { files: dt.files } });
    }
  };

  const submit = async () => {
    if (!image && !productName.trim()) { toast.error("Upload a design or type a product name"); return; }
    if (selected.length === 0) { toast.error("Pick at least one platform"); return; }
    setLoading(true); setResults([]);
    try {
      const bv = brandVoices.find(v => v.id === brandVoiceId);
      const payload = {
        productName: productName || null,
        productType,
        keywords: keywords.split(",").map(x=>x.trim()).filter(Boolean),
        platforms: selected,
        tone, language, cleanMode,
        brandVoice: bv ? { name: bv.name, vibe: bv.vibe, sample: bv.sample } : null,
        imageBase64: image?.b64 || null,
        imageMime: image?.mime || null,
      };
      const r = await http.post("/generate", payload);
      setResults(r.data.listings || []);
      toast.success(`Generated ${r.data.listings?.length || 0} listings · -1 credit`);
      onRun && onRun();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Generation failed";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="tt-card p-6">
        <h1 className="text-3xl font-black h-tight">Generate a listing</h1>
        <p className="text-sm text-zinc-500 mt-1">Upload a design and hit generate. Everything else is optional.</p>

        <div className="mt-6 grid md:grid-cols-[280px,1fr] gap-5">
          <label
            data-testid="drop-zone"
            onDrop={onDrop} onDragOver={(e)=>e.preventDefault()}
            className="relative flex flex-col items-center justify-center border-2 border-dashed border-zinc-300 rounded-xl p-6 cursor-pointer bg-zinc-50/60 hover:border-lime hover:bg-lime/5 transition-colors min-h-[220px]"
          >
            <input ref={fileRef} data-testid="file-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />
            {image ? (
              <div className="relative w-full h-full flex items-center justify-center">
                <img src={image.preview} alt="" className="max-h-40 rounded-md shadow" />
                <button
                  data-testid="clear-image-btn"
                  onClick={(e)=>{ e.preventDefault(); setImage(null); if(fileRef.current) fileRef.current.value=""; }}
                  className="absolute top-1 right-1 bg-white rounded-full p-1 shadow border border-zinc-200"
                ><X size={14} /></button>
              </div>
            ) : (
              <>
                <ImageIcon size={30} className="text-zinc-400" />
                <div className="mt-3 text-sm font-semibold text-zinc-800">Drop your design here</div>
                <div className="text-xs text-zinc-500">or click to upload · JPG, PNG, WEBP</div>
              </>
            )}
          </label>

          <div className="grid gap-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Product name (optional)</div>
                <input data-testid="product-name-input" className="input" value={productName} onChange={e=>setProductName(e.target.value)} placeholder="e.g. Retro Cat Astronaut Tee" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Product type</div>
                <input className="input" value={productType} onChange={e=>setProductType(e.target.value)} placeholder="t-shirt, mug, poster…" />
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Seed keywords (comma separated)</div>
              <input data-testid="keywords-input" className="input" value={keywords} onChange={e=>setKeywords(e.target.value)} placeholder="astronaut cat, space, retro" />
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Tone</div>
                <select data-testid="tone-select" className="input" value={tone} onChange={e=>setTone(e.target.value)}>
                  {PLATFORM_TONES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Language</div>
                <input className="input" value={language} onChange={e=>setLanguage(e.target.value)} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Brand voice</div>
                <select className="input" value={brandVoiceId} onChange={e=>setBrandVoiceId(e.target.value)}>
                  <option value="">— none —</option>
                  {brandVoices.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-zinc-700 mt-1 cursor-pointer">
              <input data-testid="clean-mode-toggle" type="checkbox" checked={cleanMode} onChange={e=>setCleanMode(e.target.checked)} />
              Sound human · no long dashes · avoid AI cliches
            </label>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Target platforms</div>
            <div className="flex gap-2">
              <button data-testid="platforms-all-btn" onClick={selectAll} className="text-xs font-semibold text-zinc-600 hover:text-zinc-900">Select all</button>
              <span className="text-zinc-300">|</span>
              <button onClick={clearAll} className="text-xs font-semibold text-zinc-600 hover:text-zinc-900">Clear</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {platforms.map(p => (
              <button
                key={p.key}
                data-testid={`platform-pill-${p.key}`}
                onClick={()=>togglePlatform(p.key)}
                className={"pill " + (selected.includes(p.key) ? "active" : "")}
              >{p.name}</button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="text-xs text-zinc-500">1 credit per generation — "all platforms" is still 1 credit.</div>
          <button
            data-testid="generate-btn"
            disabled={loading}
            onClick={submit}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Wand2 size={16} /> {loading ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {selected.slice(0,3).map((_,i)=><div key={i} className="tt-skeleton h-64" />)}
        </div>
      )}
      {!!results.length && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="results-grid">
          {results.map(l => <PlatformResultCard key={l.platform} listing={l} />)}
        </div>
      )}
    </div>
  );
}
